import React, { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";

import { useIdentityClient, useToken } from "../../hooks";

import { LogoutWarningTemplate } from "./logout-warning.template";

const WARNING_TIME_IN_MS = 1000 * 60 * 15;
const DEFAULT_REFRESH_RELOAD_TIMEOUT = 5 * 1000;

const TEST_SHOW_KEY = "PACKSIZE_LOGOUT_WARNING";

let showByDefault = Boolean(window.localStorage.getItem(TEST_SHOW_KEY));

interface LogoutWarningProps {
	readonly refreshReloadTimeout?: number;
}

export const LogoutWarning: React.FC<LogoutWarningProps> = ({ refreshReloadTimeout }) => {
	const { client } = useIdentityClient();
	const { decodedToken, processToken, clearToken } = useToken();

	const [showLogoutWarning, setShowLogoutWarning] = useState<boolean>(false);
	const [showRefreshError, setShowRefreshError] = useState<boolean>(false);
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

	const reset = useCallback(() => {
		setShowLogoutWarning(false);
		setShowRefreshError(false);
		setIsRefreshing(false);
	}, []);

	useEffect(() => {
		if (!decodedToken) {
			reset();
			return;
		}

		if (showByDefault) {
			showByDefault = false;
			setShowLogoutWarning(true);
		}

		if (!decodedToken.exp) {
			reset();
			return;
		}

		const expiryTime = DateTime.fromSeconds(decodedToken.exp);
		const timeToWarningInMs = expiryTime.diffNow().toMillis() - WARNING_TIME_IN_MS;

		const timeout = setTimeout(() => setShowLogoutWarning(true), timeToWarningInMs);

		return () => {
			clearTimeout(timeout);
		};
	}, [decodedToken, reset]);

	const handleRefreshTokenClicked = async () => {
		if (isRefreshing) {
			return;
		}

		setIsRefreshing(true);

		const newToken = await client.refreshToken();

		if (!newToken.success || !newToken.data || !newToken.data.access_token) {
			setShowRefreshError(true);
			setIsRefreshing(false);
			return;
		}

		const valid = await processToken(newToken.data.access_token);

		if (valid === true) {
			setShowLogoutWarning(false);

			setTimeout(() => window.location.reload(), Math.abs(refreshReloadTimeout || DEFAULT_REFRESH_RELOAD_TIMEOUT));
		}

		setShowRefreshError(valid === false);
		setIsRefreshing(false);
	};

	const handleLogoutClicked = () => {
		clearToken();
	};

	if (!showLogoutWarning) {
		return null;
	}

	return (
		<LogoutWarningTemplate
			onLogoutClicked={handleLogoutClicked}
			onRefreshClicked={handleRefreshTokenClicked}
			isRefreshing={isRefreshing}
			showRefreshError={showRefreshError}
		/>
	);
};
